import { retractionsById } from '@/features/leads/workflow/timeline'
import { historyBody } from '@/features/leads/workflow/historyBody'
import { cloneElement, useEffect, useMemo, useRef, useState } from 'react'
import { Ico } from '@/ui/Ico'
import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { useCorrectEntry } from '@/shared/api/leads'
import { hasPerm, useColleagues, useMe } from '@/shared/api/session'
import { useCreateNote, useDeleteNote, usePatchNote } from '@/features/leads/api/notes'
import { useCanEdit, writeControlProps } from '@/shared/write-access'
import { trackEvent } from '@/shared/api/telemetry'
import { textMeta } from '@/shared/api/trackMeta'
import { OUTCOMES, type OutcomeKind } from '@/shared/domain'
import { stageColor, stageLabel } from '@/shared/pipelineColors'
import { Z } from '@/shared/charts/theme'
import { initials } from '@/shared/lib/contact'
import { outcomeLabel } from './outcomeLabel'
import type { Lead, Note, TimelineItem, TimelineKind } from '@/shared/types'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import { useBrand } from '@/shared/white-label/useBrand'
import { Button, Eyebrow } from '@/ui'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'

const ICON: Record<TimelineKind, React.ReactElement<{ width?: number; height?: number }>> = {
  call: Ico.phone,
  email: Ico.mail,
  meeting: Ico.check,
  note: Ico.edit,
  stage: Ico.arrowR,
  handover: Ico.arrowR,
  created: Ico.bolt,
  primary_changed: Ico.starFilled,
  contact_edited: Ico.edit,
  contact_added: Ico.starFilled,
  contact_removed: Ico.x,
  correction: Ico.x,
  rating: Ico.star,
  do_not_contact: Ico.ban,
  do_not_contact_removed: Ico.check,
}

// An opaque, lightened version of a token colour — blended with white in JS
// rather than a CSS alpha background, so the timeline's connecting line
// (drawn behind every icon) never shows through the tint.
function paleTint(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16)
  const mix = (shift: number) => {
    const c = (n >> shift) & 255
    return Math.round(c * amount + 255 * (1 - amount))
  }
  return `rgb(${mix(16)}, ${mix(8)}, ${mix(0)})`
}

// Icon-circle tone per timeline kind. Only the kinds worth calling out get a
// colour; everything else (call, email, note, the bookkeeping kinds) shares
// the neutral default below.
const ICON_TONE: Partial<Record<TimelineKind, { bg: string; fg: string }>> = {
  meeting: { bg: paleTint(Z.accent, 0.22), fg: 'var(--color-ink-900)' },
  rating: { bg: paleTint(Z.accent, 0.22), fg: 'var(--color-ink-900)' },
  stage: { bg: paleTint(Z.moss, 0.16), fg: Z.moss },
  handover: { bg: paleTint(Z.clay, 0.16), fg: Z.clay },
  do_not_contact: { bg: paleTint(Z.danger, 0.16), fg: Z.danger },
}
const ICON_TONE_DEFAULT = { bg: 'var(--color-ink-100)', fg: 'var(--color-ink-700)' }

/** Entries worth retracting: the ones that also changed the lead's state. */
function isBooking(it: TimelineItem): boolean {
  return it.kind === 'meeting' || (it.kind === 'call' && it.outcome === 'booked')
}

// The outcome badge's tint — via the stage that outcome would move the lead
// to, the same association pipelineColors already draws for 'booked' sharing
// the Booked stage's colour. Outcomes with no stage (reached, voicemail,
// email_sent, referred) land on the neutral 'Other' tone.
function outcomeBadgeTone(outcome?: string): string {
  const stage = OUTCOMES[outcome as OutcomeKind]?.stage
  return paleTint(stageColor(stage ?? 'Other'), 0.2)
}

// Calendar day a timestamp falls on, in the viewer's local time — matching
// fmtDate's own day-boundary logic so a group's header and its membership
// never disagree about where "today" ends.
function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

// Compact relative time for the feed's meta line ('2 d sedan'). The exact
// timestamp is still one hover away via the Tooltip wrapping it.
function relativeShort(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
  if (days <= 0) return 'Idag'
  if (days === 1) return 'Igår'
  if (days < 30) return `${days} d sedan`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} mån sedan`
  return `${Math.floor(days / 365)} år sedan`
}

// The call outcomes, taken from the same catalogue the action buttons use, so
// this composer can never invent an outcome the dashboard does not count.
const CALL_OUTCOMES = (Object.keys(OUTCOMES) as OutcomeKind[])
  .filter(k => OUTCOMES[k].eventKind === 'call')
  .filter(k => !('needsCapture' in OUTCOMES[k] && OUTCOMES[k].needsCapture))

// Matches the notes panel this replaced, so the saving rhythm is the one BDRs
// already have in their fingers.
const DEBOUNCE_MS = 500
const SAVED_DOT_MS = 1200

/** One row of the merged stream: a recorded event, or somebody's note. */
type Row =
  | { at: string; seq: number; ev: TimelineItem; note?: undefined }
  | { at: string; seq: number; note: Note; ev?: undefined }

/** The feed, flattened: a sticky day marker, or one row under it. */
type FeedEntry =
  | { day: string; label: string; row?: undefined }
  | { day?: undefined; label?: undefined; row: Row }

interface Props {
  lead: Lead
  /** Opens the outcome flow, carrying whatever the BDR already typed. Calls and
   *  bookings route through it so the outcome, the follow-up date and the stage
   *  change are captured in the one place that knows how to do all three. */
  onAction?: (kind: OutcomeKind, comment?: string) => void
  /** Render as the right-hand panel rather than a full-width tab. */
  panel?: boolean
  onClose?: () => void
}

export default function TimelineTab({ lead, onAction, panel = false, onClose }: Props) {
  // Drafts are held per lead, not as one string. Nothing keys this component on
  // the lead, so advancing the queue does not remount it — a single draft would
  // follow the seller to the next card and save itself onto the wrong company.
  // Keeping a map also means half-typed text survives a trip to another lead.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const draft = drafts[lead.id] ?? ''
  const setDraft = (v: string) => setDrafts(d => ({ ...d, [lead.id]: v }))
  const [composer, setComposer] = useState<'note' | 'call' | 'email' | 'meeting'>('note')
  // leadId -> the note row this draft has already become, so subsequent
  // keystrokes patch it instead of stacking a new note per pause. Refs, not
  // state: the autosave effect reads them and must not re-arm when they change.
  const promotedRef = useRef<Record<string, string>>({})
  const lastSavedRef = useRef<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'dirty' | 'saving' | 'saved' | 'error'>('idle')
  const { data: me } = useMe()
  const canEdit = useCanEdit()
  const { person, anonymised } = useBrand()
  const { data: colleagues } = useColleagues()
  const correct = useCorrectEntry()
  const createNote = useCreateNote()
  const patchNote = usePatchNote()
  const deleteNote = useDeleteNote()
  const [confirming, setConfirming] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null)
  const [failed, setFailed] = useState('')

  const mayNote = hasPerm(me, 'manage_notes') && canEdit

  // `editing` and `confirming` hold ids belonging to the lead on screen. Same
  // reason as the draft map: without a remount they would point at the previous
  // card's note or timeline entry.
  useEffect(() => {
    setEditing(null)
    setConfirming(null)
    setFailed('')
    setStatus('idle')
  }, [lead.id])

  // Author-only, mirroring notes_service.update_note — anyone else gets a 404.
  // Legacy-authored notes are the exception any BDR may tidy up.
  const mayEditNote = (n: Note): boolean => {
    if (!mayNote) return false
    const who = (me?.email || '').toLowerCase()
    return n.authorEmail === 'legacy' || who === n.authorEmail.toLowerCase()
  }
  // A lead marked Kontakta ej takes notes only: the server refuses calls,
  // emails and meetings on it.
  const mayLog = hasPerm(me, 'log_calls') && canEdit && !!onAction && !lead.doNotContact
  // Marked while the Samtal, Mejl or Möte tag was selected: back to the note,
  // or the composer would show nothing at all.
  useEffect(() => {
    if (!mayLog) setComposer('note')
  }, [mayLog])

  // Display name for a BDR's email: the colleague roster's name where the app
  // has it, otherwise the part before @. White-label keeps going through
  // person(), which masks by matching the roster's emails — feeding it an
  // already-resolved name would break that match.
  const nameFor = (email?: string | null): string => {
    if (!email) return '—'
    if (anonymised) return person(email)
    return colleagues?.users.find(u => u.email.toLowerCase() === email.toLowerCase())?.name
      || email.split('@')[0]
  }

  // Events and notes in one chronology, newest first. Sorted on the timestamp
  // rather than reversing the array, because two sources have to interleave.
  // Notes sort on creation, so editing one never moves it through history.
  const items = useMemo<Row[]>(() => {
    const rows: Row[] = [
      ...lead.timeline.map((ev, i) => ({ at: ev.at, seq: i, ev })),
      ...lead.notes.map((note, i) => ({ at: note.createdAt, seq: 1e6 + i, note })),
    ]
    const editingId = composer === 'note' && draft.trim() ? promotedRef.current[lead.id] : undefined
    return rows
      .filter(r => !(r.note && editingId && r.note.id === editingId))
      .sort((a, b) => (Date.parse(b.at) - Date.parse(a.at)) || (b.seq - a.seq))
  }, [lead.timeline, lead.notes, lead.id, composer, draft])

  // The same rows, with a sticky day marker inserted ahead of each new
  // calendar day — "Idag", "Igår", then fmtDate's bare "6 okt." for anything
  // older, exactly the labels fmtDate already produces without a time part.
  const feed = useMemo<FeedEntry[]>(() => {
    const out: FeedEntry[] = []
    let lastDay = ''
    for (const row of items) {
      const day = dayKey(row.at)
      if (day !== lastDay) {
        out.push({ day, label: fmtDate(row.at) })
        lastDay = day
      }
      out.push({ row })
    }
    return out
  }, [items])

  // Ids already retracted — those entries render struck through instead of
  // disappearing, so the history still shows what happened and who undid it.
  const retracted = useMemo(() => retractionsById(lead.timeline), [lead.timeline])

  // Either the person who logged it or whoever holds the lead now: a lead that
  // changed hands brings the old owner's mistakes into the new owner's queue.
  const mayCorrect = (it: TimelineItem): boolean => {
    if (!it.id || !isBooking(it)) return false
    if (retracted.has(it.id)) return false
    const who = (me?.email || '').toLowerCase()
    if (!who) return false
    return hasPerm(me, 'manage_users')
      || who === (it.by || '').toLowerCase()
      || who === (lead.assignedTo || '').toLowerCase()
  }

  const runCorrect = async (entryId: string) => {
    setFailed('')
    const kind = lead.timeline.find(it => it.id === entryId)?.kind
    try {
      await correct.mutateAsync({ leadId: lead.id, entryId, reason: 'Logged by mistake' })
      setConfirming(null)
    } catch (e) {
      trackEvent('write_failed', { op: 'retract' }, lead.id)
      setFailed(e instanceof Error ? e.message : 'Could not retract that entry.')
      return
    }
    // A booking coming back out of the statistics is the one write that changes
    // a number someone is measured on, and the corrections endpoint records
    // nothing of its own.
    trackEvent('entry_retracted', { kind }, lead.id)
  }

  // Writes a note, not a timeline entry. What a BDR types is theirs and stays
  // editable; the stream's other rows are things the system recorded and can
  // only be retracted. The tag is what decides which of the two you get.
  //
  // Autosave, but only under the Note tag. The composer cannot save eagerly for
  // the others: a call needs its outcome and a booking needs its date, and a
  // half-typed call summary is not a note. First pause creates the note row,
  // later pauses patch that same row.
  useEffect(() => {
    if (composer !== 'note' || !mayNote) return
    const body = draft.trim()
    if (!body || lastSavedRef.current[lead.id] === body) return
    setStatus('dirty')
    const leadId = lead.id
    const t = window.setTimeout(async () => {
      setStatus('saving')
      try {
        const noteId = promotedRef.current[leadId]
        if (noteId) {
          // Autosave patches this same row on every later pause. One note is one
          // write action, so only the creation is reported — a per-pause event
          // would turn a paragraph into a dozen rows that say nothing extra.
          await patchNote.mutateAsync({ leadId, noteId, body })
        } else {
          const updated = await createNote.mutateAsync({ leadId, body })
          // The notes endpoint records no usage event of its own — the server's
          // `note_added` covers only the timeline-event note kind, which this
          // composer does not use.
          trackEvent('note_written', textMeta(body), leadId)
          // Identify the row we just made by author and body rather than by
          // taking the newest note, so a colleague saving at the same moment
          // cannot make us adopt theirs.
          const mine = (updated.notes || [])
            .filter(n => n.authorEmail === me?.email && n.body === body)
            .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
          if (mine[0]) promotedRef.current[leadId] = mine[0].id
        }
        lastSavedRef.current[leadId] = body
        setStatus('saved')
      } catch {
        trackEvent('write_failed', { op: 'note' }, leadId)
        setStatus('error')
      }
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(t)
    // The mutation objects get a new identity every render; including them would
    // re-arm the timer continuously and save mid-word.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, composer, lead.id, mayNote, me?.email])

  // Let the saved dot fade rather than sit there.
  useEffect(() => {
    if (status !== 'saved') return
    const t = window.setTimeout(() => setStatus('idle'), SAVED_DOT_MS)
    return () => window.clearTimeout(t)
  }, [status])

  const saveEdit = async () => {
    if (!editing || patchNote.isPending) return
    const body = editing.body.trim()
    if (!body) return
    setFailed('')
    try {
      await patchNote.mutateAsync({ leadId: lead.id, noteId: editing.id, body })
      setEditing(null)
    } catch (e) {
      trackEvent('write_failed', { op: 'note_edit' }, lead.id)
      setFailed(e instanceof Error ? e.message : 'Could not save that edit.')
      return
    }
    trackEvent('note_edited', textMeta(body), lead.id)
  }

  const removeNote = async (noteId: string) => {
    if (deleteNote.isPending) return
    setFailed('')
    try {
      await deleteNote.mutateAsync({ leadId: lead.id, noteId })
      setEditing(null)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'Could not delete that note.')
    }
  }

  // Everything that is not a note hands the typed text to the outcome flow,
  // which owns the outcome, the follow-up date, the rating and the stage. The
  // composer deliberately does not write these entries itself: a call with no
  // outcome counts as neither reached nor booked on the dashboard, and a bare
  // meeting entry books the lead without ever asking when.
  const handOff = async (kind: OutcomeKind) => {
    const body = draft.trim()
    const leadId = lead.id
    // Autosave may already have made this text a note before the BDR decided it
    // was a call. Take the note back rather than leaving it beside the entry
    // saying the same thing twice.
    const noteId = promotedRef.current[leadId]
    if (noteId) {
      delete promotedRef.current[leadId]
      delete lastSavedRef.current[leadId]
      try {
        await deleteNote.mutateAsync({ leadId, noteId })
      } catch {
        setFailed('Logged it, but the draft note could not be cleaned up.')
      }
    }
    setDrafts(d => ({ ...d, [leadId]: '' }))
    setStatus('idle')
    onAction?.(kind, body || undefined)
  }

  const noteHelper = status === 'saving' ? 'Sparar…'
    : status === 'saved' ? 'Sparat'
    : status === 'error' ? 'Sparades inte'
    : 'Sparas automatiskt. Din att redigera, och den räknas inte som kontakt.'

  const rowAnim = {
    initial: { opacity: 0, y: -6 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 },
    transition: { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] as const },
  }

  const body = (
    <>
      <div
        className={cls(
          'bg-white border border-ink-200 rounded-lg',
          'focus-within:border-ink-400 focus-within:ring-2 focus-within:ring-ink-900/8',
        )}
      >
        {(mayNote || mayLog) && (
          <textarea
            className="w-full border-0 px-3 py-2 outline-none bg-transparent text-[13px] resize-y min-h-[3rem] rounded-t-lg"
            placeholder={mayLog
              ? 'Vad hände? Skriv det här och välj sedan vad det var.'
              : 'Skriv en anteckning — din att redigera, och den räknas inte som kontakt'}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={2}
          />
        )}

        {composer === 'call' && mayLog && (
          <div className="px-3 pb-2">
            <div className="text-[11px] text-ink-500 mb-1">
              Hur gick det? Utfallet styr stadiet och uppföljningen.
            </div>
            <div className="flex flex-wrap gap-1">
              {CALL_OUTCOMES.map(k => (
                <button
                  key={k}
                  type="button"
                  className="text-[11px] px-1.5 py-0.5 rounded border border-ink-200 bg-white text-ink-700 cursor-pointer hover:border-ink-1000 hover:text-ink-1000"
                  onClick={() => handOff(k)}
                >
                  {OUTCOMES[k].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {composer === 'email' && mayLog && (
          <div className="px-3 pb-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-ink-500">
              Loggar mejlet och sätter en uppföljning.
            </span>
            <Button variant="primary" size="sm" onClick={() => handOff('email_sent')}>
              {OUTCOMES.email_sent.label}
            </Button>
          </div>
        )}

        {composer === 'meeting' && mayLog && (
          <div className="px-3 pb-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-ink-500">
              Bokar mötet och frågar efter datumet.
            </span>
            <Button variant="primary" size="sm" onClick={() => handOff('booked')}>
              {OUTCOMES.booked.label}
            </Button>
          </div>
        )}

        {!mayNote && !mayLog && (
          <div className="px-3 pb-2 text-[11px] text-ink-500">
            {canEdit
              ? 'Du har inte behörighet att logga aktivitet på den här leaden.'
              : 'Skrivskyddad — aktivera redigeringsläge för att logga något här.'}
          </div>
        )}

        {failed && <div className="px-3 pb-2 text-[11px] text-danger">{failed}</div>}

        {(mayNote || mayLog) && (
          <div className="flex flex-col gap-1 px-2 py-1.5 border-t border-ink-200 rounded-b-lg bg-paper/60">
            <div className="flex gap-0.5">
              {([
                { v: 'note', label: 'Anteckning', icon: Ico.edit, enabled: mayNote },
                { v: 'call', label: 'Samtal', icon: Ico.phone, enabled: mayLog },
                { v: 'email', label: 'Mejl', icon: Ico.mail, enabled: mayLog },
                { v: 'meeting', label: 'Möte', icon: Ico.check, enabled: mayLog },
              ] as const).filter(o => o.enabled).map(o => (
                <button
                  key={o.v}
                  type="button"
                  className={cls(
                    'inline-flex items-center gap-1 text-[11px] px-1.5 py-1 rounded cursor-pointer',
                    'whitespace-nowrap transition-colors [&_svg]:w-2.5 [&_svg]:h-2.5 [&_svg]:shrink-0',
                    composer === o.v
                      ? 'bg-ink-100 text-ink-1000 font-medium'
                      : 'text-ink-500 hover:text-ink-1000 hover:bg-ink-50',
                  )}
                  aria-pressed={composer === o.v}
                  onClick={() => { setComposer(o.v); setFailed('') }}
                >
                  {o.icon}
                  {o.label}
                </button>
              ))}
            </div>
            {composer === 'note' && mayNote && (
              <span className={cls('text-[11px] px-0.5', status === 'error' ? 'text-danger' : 'text-ink-500')}>
                {noteHelper}
              </span>
            )}
          </div>
        )}
      </div>

      {feed.length === 0 ? (
        <div className="bg-white border border-ink-200 rounded-lg">
          <Empty size="sm">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                {cloneElement(Ico.edit, { width: 18, height: 18 })}
              </EmptyMedia>
              <EmptyTitle>Inget här än</EmptyTitle>
              <EmptyDescription>
                Skriv en anteckning eller logga ett samtal så dyker det upp här.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <ol className="relative list-none p-0 m-0 bg-white border border-ink-200 rounded-lg">
          <li aria-hidden="true" className="absolute left-6 top-4 bottom-4 w-px bg-ink-300 pointer-events-none" />
          <AnimatePresence initial={false}>
            {feed.map((f, i) => {
              if (f.day) {
                return (
                  <li
                    key={`day-${f.day}`}
                    className="sticky top-0 z-10 bg-white px-3.5 pt-3 pb-1 first:pt-2.5"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                      {f.label}
                    </span>
                  </li>
                )
              }

              const row = f.row!

              if (row.note) {
                const n = row.note
                const legacy = n.authorEmail === 'legacy'
                const edited = n.updatedAt !== n.createdAt
                return (
                  <m.li
                    key={`n-${n.id}`}
                    className="group grid grid-cols-[20px_1fr] gap-2.5 px-3.5 py-2"
                    {...rowAnim}
                  >
                    {legacy ? (
                      <span className="w-5 h-5 rounded-full grid place-items-center mt-0.5 relative z-10"
                            style={{ background: ICON_TONE_DEFAULT.bg, color: ICON_TONE_DEFAULT.fg }}>
                        {cloneElement(Ico.edit, { width: 11, height: 11 })}
                      </span>
                    ) : (
                      <Avatar size="xs" className="mt-0.5 relative z-10">
                        <AvatarFallback className="bg-ink-100 text-ink-700">
                          {initials(nameFor(n.authorEmail))}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="min-w-0">
                      <div className={cls('min-w-0', !legacy && 'bg-ink-50 rounded-md -mx-2 px-2 py-1.5')}>
                        <div className="text-[13px] text-ink-1000 font-medium">
                          {legacy ? 'Anteckning · importerad' : 'Anteckning'}
                        </div>
                        <div className="text-[12px] text-ink-500 mt-0.5 flex items-center gap-1">
                          {!legacy && <span>{nameFor(n.authorEmail)} ·</span>}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default">{relativeShort(n.createdAt)}</span>
                            </TooltipTrigger>
                            <TooltipContent>{fmtDate(n.createdAt, { time: true })}</TooltipContent>
                          </Tooltip>
                        </div>
                        {editing?.id === n.id ? (
                          <div className="mt-1">
                            <textarea
                              className="w-full text-[13px] border border-ink-200 rounded px-2 py-1 bg-white resize-y"
                              value={editing.body}
                              rows={3}
                              onChange={e => setEditing({ id: n.id, body: e.target.value })}
                            />
                            <div className="flex gap-2 mt-1.5">
                              <button
                                type="button"
                                disabled={!editing.body.trim() || patchNote.isPending}
                                className="px-2.5 py-1 text-[11px] font-semibold rounded bg-ink-1000 text-paper border-0 cursor-pointer disabled:opacity-50"
                                onClick={saveEdit}
                              >
                                {patchNote.isPending ? 'Sparar…' : 'Spara'}
                              </button>
                              <button
                                type="button"
                                className="px-2.5 py-1 text-[11px] rounded border border-ink-200 bg-transparent text-ink-700 cursor-pointer"
                                onClick={() => { setEditing(null); setFailed('') }}
                              >
                                Avbryt
                              </button>
                              <button
                                type="button"
                                disabled={deleteNote.isPending}
                                className="px-2.5 py-1 text-[11px] rounded border-0 bg-transparent text-ink-500 underline cursor-pointer ml-auto hover:text-danger disabled:opacity-50"
                                onClick={() => removeNote(n.id)}
                              >
                                {deleteNote.isPending ? 'Tar bort…' : 'Ta bort'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className={cls('text-[13px] mt-1 leading-[1.5] whitespace-pre-wrap',
                                                legacy ? 'text-ink-500 italic' : 'text-ink-700')}>
                              {n.body}
                            </div>
                            {(edited || mayEditNote(n)) && (
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <span className="text-[11px] text-ink-500">
                                  {edited && `redigerad ${fmtDate(n.updatedAt, { time: true })}`}
                                </span>
                                {mayEditNote(n) && (
                                  <button
                                    type="button"
                                    className={cls(
                                      'shrink-0 text-[11px] text-ink-500 underline bg-transparent border-0 p-0',
                                      'cursor-pointer hover:text-ink-1000 opacity-0 transition-opacity',
                                      'group-hover:opacity-100 focus-visible:opacity-100',
                                    )}
                                    onClick={() => { setEditing({ id: n.id, body: n.body }); setFailed('') }}
                                  >
                                    Redigera
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </m.li>
                )
              }

              const it = row.ev!
              const tone = ICON_TONE[it.kind] ?? ICON_TONE_DEFAULT
              // Falls back for a kind the frontend maps don't know yet — the API
              // accepts timeline kinds this union hasn't caught up with, and a
              // missing entry here used to throw cloneElement(undefined) and blank
              // the whole app instead of just rendering that one row plainly.
              const iconEl = ICON[it.kind] ?? Ico.dots
              const sizedIcon = cloneElement(iconEl, { width: 11, height: 11 })
              const gone = it.id ? retracted.get(it.id) : undefined
              const survivingBookings = lead.timeline.filter(
                o => isBooking(o) && o.id !== it.id && !(o.id && retracted.has(o.id)),
              ).length
              const onlyBooking = isBooking(it) && survivingBookings === 0

              return (
                <m.li
                  key={it.id || `e-${i}`}
                  className={cls(
                    'grid grid-cols-[20px_1fr] gap-2.5 px-3.5 py-2',
                    gone && 'opacity-55',
                  )}
                  {...rowAnim}
                >
                  <span
                    className="w-5 h-5 rounded-full grid place-items-center mt-0.5 relative z-10"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    {sizedIcon}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={cls('text-[13px] text-ink-1000 font-medium', gone && 'line-through')}>
                        {it.kind === 'call' && 'Samtal'}
                        {it.kind === 'email' && 'Mejl'}
                        {it.kind === 'meeting' && 'Möte'}
                        {it.kind === 'note' && 'Loggad anteckning'}
                        {it.kind === 'stage' && `Stadie · ${stageLabel(it.from ?? '')} → ${stageLabel(it.to ?? '')}`}
                        {it.kind === 'handover' && 'Överlämning'}
                        {it.kind === 'created' && 'Lead skapad'}
                        {it.kind === 'primary_changed' && 'Primär kontakt ändrad'}
                        {it.kind === 'contact_edited' && 'Kontakt redigerad'}
                        {it.kind === 'contact_added' && 'Kontakt tillagd'}
                        {it.kind === 'contact_removed' && 'Kontakt borttagen'}
                        {it.kind === 'correction' && 'Återkallad — loggad av misstag'}
                        {it.kind === 'rating' && 'Betyg'}
                        {it.kind === 'do_not_contact' && 'Markerad Kontakta ej'}
                        {it.kind === 'do_not_contact_removed' && 'Kontakta ej borttagen'}
                      </span>
                      {it.kind === 'call' && (
                        <Badge
                          variant="outline"
                          className="border-transparent text-ink-900 font-normal"
                          style={{ background: outcomeBadgeTone(it.outcome) }}
                        >
                          {outcomeLabel(it.outcome)}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[12px] text-ink-500 mt-0.5 flex items-center gap-1">
                      <span>{nameFor(it.by)} ·</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">{relativeShort(it.at)}</span>
                        </TooltipTrigger>
                        <TooltipContent>{fmtDate(it.at, { time: true })}</TooltipContent>
                      </Tooltip>
                    </div>
                    {it.body && <div className="text-[13px] text-ink-700 mt-1.5 leading-[1.5]">{historyBody(it)}</div>}

                    {gone && (
                      <div className="text-[11px] text-ink-500 mt-1">
                        återkallad av {nameFor(gone.by)} · {fmtDate(gone.at, { time: true })}
                      </div>
                    )}

                    {mayCorrect(it) && confirming !== it.id && (
                      <button
                        type="button"
                        className="mt-1.5 text-[11px] text-ink-500 underline bg-transparent border-0 p-0 cursor-pointer hover:text-danger disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-500"
                        {...writeControlProps(canEdit)}
                        onClick={() => { setConfirming(it.id!); setFailed('') }}
                      >
                        Loggad av misstag?
                      </button>
                    )}

                    {confirming === it.id && (
                      <div className="mt-2 border border-ink-200 rounded p-2.5 bg-paper">
                        <div className="text-[12px] text-ink-1000 font-semibold">
                          Återkalla den här bokningen?
                        </div>
                        <ul className="text-[11px] text-ink-700 mt-1 mb-2 pl-4 list-disc leading-[1.6]">
                          <li>Posten finns kvar i historiken, överstruken.</li>
                          <li>
                            {onlyBooking
                              ? 'Leaden slutar räknas som bokad och återgår till sitt tidigare stadie.'
                              : `${survivingBookings === 1 ? 'En annan post' : `${survivingBookings} andra poster`} på den här leaden gör fortfarande anspråk på bokningen, så den räknas fortfarande som bokad i översikten. Återkalla ${survivingBookings === 1 ? 'den också' : 'dem också'}.`}
                          </li>
                          <li>Använd bara det här om mötet aldrig bokades. Om mötet
                            ställdes in, sätt mötesutfallet istället så att förlusten räknas.</li>
                        </ul>
                        {failed && (
                          <div className="text-[11px] text-danger mb-2">{failed}</div>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={correct.isPending}
                            className="px-2.5 py-1 text-[11px] font-semibold rounded bg-ink-1000 text-paper border-0 cursor-pointer disabled:opacity-50"
                            onClick={() => runCorrect(it.id!)}
                          >
                            {correct.isPending ? 'Återkallar…' : 'Ja, återkalla'}
                          </button>
                          <button
                            type="button"
                            className="px-2.5 py-1 text-[11px] rounded border border-ink-200 bg-transparent text-ink-700 cursor-pointer"
                            onClick={() => { setConfirming(null); setFailed('') }}
                          >
                            Behåll
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </m.li>
              )
            })}
          </AnimatePresence>
        </ol>
      )}
    </>
  )

  if (!panel) {
    return <div className="flex flex-col gap-3 p-6 max-w-[720px] mx-auto">{body}</div>
  }

  return (
    <aside className="bg-white border-l border-ink-200 flex flex-col h-full min-h-0 w-full lg:w-[320px]">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-ink-200 bg-paper">
        <div className="min-w-0">
          <Eyebrow size="sm" gap="none">
            Aktivitet på
          </Eyebrow>
          <div className="text-[13px] font-semibold text-ink-1000 mt-0.5 truncate" title={lead.company}>
            {lead.company}
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 bg-transparent border border-ink-200 w-6 h-6 rounded grid place-items-center text-ink-500 hover:text-ink-1000 hover:border-ink-1000 cursor-pointer p-0"
            aria-label="Dölj aktivitet"
            title="Dölj aktivitet"
          >
            {cloneElement(Ico.x, { width: 12, height: 12 })}
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-auto flex flex-col gap-3 p-3">{body}</div>
    </aside>
  )
}
